import { ALL_CHARACTER_CLIPS, type CharacterArt, type CharacterClip, type CharacterDefinition } from './CharacterTypes';

const RUNNER_SKELETON = 'SK_BAURUN_Humanoid_V1';
const RUNNER_ANIMATION_SET = 'BAURUN_RUNNER_V1';
/** Menu art folder (public/assets/ui/) – the user's final artwork, see the README there. */
const UI_ART = '/assets/ui';

/** Menu art file slots of a character: card portrait and standing figure. */
const menuArt = (runtimeId: string, figure: string, alt: string): CharacterArt => ({
  portraitUrl: `${UI_ART}/characters/${runtimeId.toLowerCase()}.jpg`,
  // WebP with alpha, made from the PNG (tools/blender/ui/character_portraits.py writes the PNG): 1.2 MB -> 0.14 MB
  figureUrl: `${UI_ART}/${figure}.webp`,
  alt,
});

/** BAURUN_RUNNER_V1 clip names: ANIM_<State> (CHARACTER_RIG_STANDARD.md). */
const runnerClips = (): Partial<Record<CharacterClip, string>> =>
  Object.fromEntries(ALL_CHARACTER_CLIPS.map((clip) => [clip, `ANIM_${clip}`]));

export const CHARACTERS: readonly CharacterDefinition[] = [
  {
    id: 'CHAR_02_CARPENTER_M',
    displayName: 'Zimmerer',
    motto: 'Stark. Präzise. Traditionell.',
    assetId: 'CHAR_02_CARPENTER_M',
    skeleton: RUNNER_SKELETON,
    animationSet: RUNNER_ANIMATION_SET,
    clips: runnerClips(),
    proceduralPose: false,
    adMaterial: 'MAT_CHAR_AD_BACK',
    art: menuArt('CHAR_02_CARPENTER_M', 'figure_carpenter', 'Zimmerer mit Bauhelm, Holzbalken auf der Schulter'),
  },
  {
    // Female electrician (1.65 m) on the same rig and animation set as the carpenter.
    id: 'CHAR_01_ELECTRICIAN_F',
    displayName: 'Elektrikerin',
    motto: 'Clever. Präzise. Immer unter Strom.',
    assetId: 'CHAR_01_ELECTRICIAN_F',
    skeleton: RUNNER_SKELETON,
    animationSet: RUNNER_ANIMATION_SET,
    clips: runnerClips(),
    proceduralPose: false,
    adMaterial: 'MAT_CHAR_AD_BACK',
    art: menuArt('CHAR_01_ELECTRICIAN_F', 'figure_electrician', 'Elektrikerin mit Bauhelm, Schraubendreher und Kabelrolle'),
  },
  {
    // Greybox placeholder – kept as fallback and for tests.
    id: 'CHAR_WORKER_PLAYER',
    displayName: 'Bauarbeiter (Platzhalter)',
    motto: '',
    assetId: 'CHAR_WORKER_PLAYER',
    skeleton: null,
    animationSet: null,
    clips: {},
    proceduralPose: true,
    adMaterial: null,
    art: null,
  },
];

export class CharacterCatalog {
  private readonly byId: ReadonlyMap<string, CharacterDefinition>;

  constructor(characters: readonly CharacterDefinition[]) {
    this.byId = new Map(characters.map((c) => [c.id, c]));
    if (this.byId.size !== characters.length) throw new Error('Duplicate character id in the catalog');
  }

  get(id: string): CharacterDefinition {
    const character = this.byId.get(id);
    if (!character) throw new Error(`Unknown character ${id}`);
    return character;
  }

  has(id: string): boolean {
    return this.byId.has(id);
  }

  all(): readonly CharacterDefinition[] {
    return [...this.byId.values()];
  }
}
